import React, { FC, memo, useMemo } from 'react';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import DateRangeInput from '@zvuk-b2b/react-uikit/ui/DateInput/DateRangeInput';

import { TEXTS } from './texts';
import {
  DATA_TEST_IDS,
  DATE_RANGE_DESKTOP_MONTHS_SHOWN,
  DATE_RANGE_MOBILE_MONTHS_SHOWN,
  MAX_DATE,
  MIN_DATE,
} from './consts';
import type { DefaultAdCampaignsReportModalProps } from './types';

import styles from './index.module.scss';

const DefaultAdCampaignsReportModal: FC<DefaultAdCampaignsReportModalProps> = ({
  close,
  closeButtonText,
  endDate,
  isMobile,
  isRequestDisabled,
  loading,
  modalTitle,
  onDateRangeChange,
  onRequestSubmit,
  requestButtonText,
  startDate,
}) => {
  const dateRangeMonthsShown = isMobile ? DATE_RANGE_MOBILE_MONTHS_SHOWN : DATE_RANGE_DESKTOP_MONTHS_SHOWN;

  const actionButtons = useMemo(
    () => [
      <Button
        elementId="button_submit_request_ad_campaigns_report_modal"
        key="button_submit_request_ad_campaigns_report_modal"
        type={ButtonType.PRIMARY}
        onClick={onRequestSubmit}
        isLoading={loading}
        isDisabled={isRequestDisabled}>
        {requestButtonText}
      </Button>,
      <Button
        elementId="button_cancel_request_ad_campaign_report_modal"
        key="button_cancel_request_ad_campaign_report_modal"
        type={ButtonType.SECONDARY}
        onClick={close}>
        {closeButtonText}
      </Button>,
    ],
    [close, closeButtonText, isRequestDisabled, loading, onRequestSubmit, requestButtonText]
  );

  return (
    <ModalScreen
      title={modalTitle}
      onClose={close}
      elementId={DATA_TEST_IDS.DEFAULT_MODAL}
      actionButtons={actionButtons}>
      <div
        className={styles.content}
        data-testid={DATA_TEST_IDS.DEFAULT_DESCRIPTION}>
        {TEXTS.REQUEST_REPORT_MODAL_CONTENT}
      </div>
      <div data-testid={DATA_TEST_IDS.DEFAULT_DATE_RANGE}>
        <DateRangeInput
          elementId="input_ad_campaigns_report_date_range"
          startDate={startDate}
          endDate={endDate}
          onChange={onDateRangeChange}
          popperPlacement="right"
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          label={TEXTS.LABEL}
          containerClassName={styles.dateRangeInput}
          showPreviousMonths={false}
          openToDate={isMobile ? MIN_DATE : startDate || new Date()}
          monthsShown={dateRangeMonthsShown}
          focusSelectedMonth={!isMobile}
          isClearable
          withPortal={isMobile}
        />
      </div>
      <div
        className={styles.footer}
        data-testid={DATA_TEST_IDS.DEFAULT_FOOTER}>
        {TEXTS.HINT}
      </div>
    </ModalScreen>
  );
};

export default memo(DefaultAdCampaignsReportModal);
